import { useState, type ReactNode } from 'react';
import './expandrow.css';

const isMobile = () => window.matchMedia('(max-width: 719px)').matches;

interface Props {
  /** Die Kurzansicht auf dem Handy, höchstens drei Zeilen. Sie steht nur unter 720 Pixel, am Desktop gilt die Tabelle. */
  summary: ReactNode;
  className?: string;
  /** Anker, zum Beispiel für die Buchstabenleiste */
  id?: string;
  /** Die Zellen der Tabelle. Alles, was die Kurzansicht nicht zeigt, erscheint auf dem Handy erst nach dem Aufklappen. */
  children: ReactNode;
}

/**
 * Zeile einer Liste, die auf dem Handy zugeklappt ist: Sie zeigt nur die Kurzansicht (`summary`) und klappt per Tipp auf die Karte oder den Pfeil auf. Am
 * Desktop ist sie eine gewöhnliche Tabellenzeile. Gehört in eine `table--cards`. Die erste Zelle der Tabelle (der Titel) trägt die Klasse `cell-desktop-title`,
 * weil die Kurzansicht sie auf dem Handy ersetzt. Regel für alle Listen, die groß werden können: siehe „UI-Regeln“ in docs/architektur.md.
 */
export function ExpandRow({ summary, className = '', id, children }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <tr
      id={id}
      className={`exprow${open ? ' is-expanded' : ''} ${className}`.trim()}
      onClick={(e) => {
        // Ein Tipp auf einen Link oder Knopf in der Zeile gehört dem Link oder Knopf
        if (isMobile() && !(e.target as HTMLElement).closest('a, button, input, select, label')) setOpen((o) => !o);
      }}
    >
      <td className="cell-summary">{summary}</td>
      {children}
      <td className="cell-expand">
        <button type="button" className="exprow__toggle" aria-expanded={open} aria-label={open ? 'Weniger anzeigen' : 'Mehr anzeigen'} onClick={() => setOpen((o) => !o)}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </td>
    </tr>
  );
}

/** Drei Zeilen der Kurzansicht: Titel (links) mit etwas rechts daneben, darunter zwei Zeilen Nebeninfo */
export function Summary({ title, aside, line2, line3 }: { title: ReactNode; aside?: ReactNode; line2?: ReactNode; line3?: ReactNode }) {
  return (
    <div className="sum">
      <div className="sum__l1">
        <span className="sum__title">{title}</span>
        {aside && <span className="sum__aside">{aside}</span>}
      </div>
      {line2 && <div className="sum__l2">{line2}</div>}
      {line3 && <div className="sum__l3">{line3}</div>}
    </div>
  );
}
